import React, { useState, useEffect, useRef } from "react";
import BootstrapSwitchButton from "bootstrap-switch-button-react";
import { Row, Col } from "react-bootstrap";
import "./SingleLoan.css";
import { useForm, FormProvider } from "react-hook-form";
import Inputmask from "inputmask";
import { commonService, toasterService } from "../../services";
import { FormField } from "../../components/FormField/FormField";
import { FormSections } from "../../components/FormSections/FormSections";
import { EligibilityTip } from "../../components/EligibilityTip/EligibilityTip";
import { RunHistoryLayout, useRunHistory, buildRunLabel } from "../../components/RunHistory/RunHistory";
import { EncompassPrefill } from "../../components/EncompassPrefill/EncompassPrefill";
import { OutputReports } from "../OutputReports/OutputReports";
import { useAuthState } from "../../contexts/context";
import { Role } from "../../helpers";

export function SingleLoan() {
  const [elements, setElements] = useState(null);
  const [selectedOption, setSelectedOption] = useState(true);
  const [loading, showLoader] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [outputData, setOutputData] = useState(null);
  const runHistory = useRunHistory("bf-runs-single");

  const methods = useForm();
  const userDetails = useAuthState();
  // Lock date from a pulled Encompass loan (display only — the server derives
  // the real pricing date from the signed prefill token).
  const [pulledLock, setPulledLock] = useState(null);
  const toaster = useRef(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const fields = elements ?? [];

  const onSubmit = (data) => {
    if (toaster) {
      toasterService.dismiss(toaster.current);
    }
    if (!selectedOption) {
      commonService
        .submitPriceLoanForm(data)
        .then((res) => {
          setFormSuccess(true);
          getOutputReport(res.data.data, 0, data);
          window.scrollTo({ top: 0, behavior: "smooth" });
        })
        .catch((error) => {
          toaster.current = toasterService.error((error?.response?.data?.message||"Something went wrong. Please try again."), {
            hideProgressBar: true,
            autoClose: false,
          });
        });
    } else {
      commonService
        .submitInterestLoanForm(data)
        .then((res) => {
          setFormSuccess(true);
          getOutputReport(res.data.data, 1, data);
          window.scrollTo({ top: 0, behavior: "smooth" });
        })
        .catch((error) => {
          toaster.current = toasterService.error((error?.response?.data?.message||"Something went wrong. Please try again."), {
            hideProgressBar: true,
            autoClose: false,
          });
        });
    }
  };

  useEffect(() => {
    getFormFields(true);
    setFormSuccess(false);
    setReportSuccess(false);
    // Load this user's past runs into the history sidebar (metadata only).
    commonService
      .getMyRuns()
      .then((resp) => runHistory.seedServerRuns(resp.data))
      .catch(() => {});
  }, []);

  // The fixed anchored submit bar covers the viewport bottom while this page is
  // mounted; flag <body> so the site footer gets bottom clearance (index.css).
  useEffect(() => {
    document.body.classList.add("bf-has-submitbar");
    return () => document.body.classList.remove("bf-has-submitbar");
  }, []);

  // Select a run in the TOC; lazy-load its output if it's a past (server) run.
  const handleSelectRun = (id) => {
    runHistory.selectRun(id);
    const run = runHistory.runs.find((r) => r.id === id);
    if (run && !run.outputData && run.serverId) {
      commonService
        .loadMyRun(run.serverId)
        .then((resp) => runHistory.setRunOutput(id, resp.data))
        .catch((e) =>
          toasterService.error(
            e?.response?.data?.message ||
              "Couldn't load that run. Please try again."
          )
        );
    }
  };

  // ---- "Go back" restore: repopulate the form from the viewed run ----------
  // Same mechanism as the Encompass prefill (the proven programmatic-populate
  // path): setValue per field; selects get {label,value} option objects so the
  // visible dropdown stays in sync (SelectField watches its form value).
  // Deliberately NOT restored: origination_date — a re-run prices on today's
  // sheet; backdating stays an explicit act, never a restore side effect.
  const [pendingRestore, setPendingRestore] = useState(null);

  // Match a raw ENGINE value back to its dropdown option. Case-insensitive:
  // the tape stores option VALUES ("CASH-OUT REFINANCE") while the dropdown
  // displays LABELS ("Cash-Out Refinance") — a strict compare left restored
  // selects shouting the engine value (owner-reported). Handles option lists
  // of plain strings too.
  const matchOption = (opts, raw) => {
    const rl = String(raw).trim().toLowerCase();
    return (Array.isArray(opts) ? opts : [])
      .map((o) => (typeof o === "string" ? { label: o, value: o } : o))
      .find(
        (o) =>
          o &&
          (String(o.value).trim().toLowerCase() === rl ||
            String(o.label).trim().toLowerCase() === rl)
      );
  };

  const applyInputValues = (iv, fieldList) => {
    let applied = 0;
    (fieldList || []).forEach((f) => {
      const raw = iv[f.field_id];
      if (raw === undefined) return;
      if (f.field_id === "origination_date") return;
      let val = raw;
      if ((f.field_type || "").split(":")[0] === "DD") {
        let opts = f.field_options;
        if (typeof opts === "string") {
          try {
            opts = JSON.parse(opts);
          } catch (e) {
            opts = [];
          }
        }
        const hit = matchOption(opts, raw);
        // remote-option selects (state, zip) have no local list; render the
        // raw pair, exactly as the Encompass prefill payload does. For the
        // state that means "CA" not "California" — owner's preference
        // (2026-08-28): the abbreviation, no reason to be verbose.
        val = hit || { label: raw, value: raw };
      }
      methods.setValue(f.field_id, val, {
        shouldValidate: false,
        shouldDirty: true,
      });
      applied += 1;
    });
    // The exception dropdown is hand-rendered (not in the DB field list) and
    // internal-only; restoring it for externals would be discarded server-side
    // anyway, so don't even echo it.
    if (
      iv.exception_pricing !== undefined &&
      userDetails.role === Role.Internal
    ) {
      methods.setValue("exception_pricing", iv.exception_pricing);
      applied += 1;
    }
    if (applied > 0) {
      toasterService.success(
        applied + " fields loaded from the selected run — review, then re-run."
      );
    }
  };

  // Back to the form. If the viewed run carries its raw inputs, load them in —
  // switching the Rate/Price toggle first when the run is the other kind (the
  // restore then applies once that form's fields have mounted).
  const goBackToForm = () => {
    const run = runHistory.activeRun;
    const od = run && run.outputData;
    const iv = od && od.inputValues;
    if (iv && Object.keys(iv).length > 0) {
      const isPriceRun = String(od.runid || "").indexOf("price_input_") === 0;
      const wantRateForm = !isPriceRun;
      if (wantRateForm !== selectedOption) {
        setPendingRestore(iv);
        getFormFields(wantRateForm);
      } else {
        applyInputValues(iv, fields);
      }
    }
    runHistory.newRun();
  };

  useEffect(() => {
    if (pendingRestore && elements && elements.length > 0 && !loading) {
      applyInputValues(pendingRestore, elements);
      setPendingRestore(null);
    }
  }, [elements, loading]);

  function getOutputReport(filename, type, inputs) {
    commonService
      .generateReport({ file: filename, type: type })
      .then((resp) => {
        setOutputData(resp.data);
        setFormSuccess((formSuccess) => (formSuccess = false));
        setReportSuccess((reportSuccess) => (reportSuccess = true));
        runHistory.addRun({
          filename,
          type,
          isLC: false,
          product: type === 0 ? "Price" : "Rate",
          label: buildRunLabel(inputs, type === 0 ? "Price" : "Rate"),
          outputData: resp.data,
        });
      })
      .catch((error) => {
        toasterService.error((error?.response?.data?.message||"Something went wrong. Please try again."), {
          hideProgressBar: true,
          autoClose: false,
        });
        setFormSuccess((formSuccess) => (formSuccess = false));
      });
  }

  function getFormFields(value) {
    if (value) {
      // Call I form data
      showLoader(true);
      // Fetch by the user's ACTUAL role. This page hardcoded 'i', so external
      // users were served the hidden=1 internal-only fields (price target,
      // yield target, pricing date, HPI knobs) that the DB marks internal.
      commonService
        .getFormFields("I", userDetails.role === Role.Internal ? "i" : "e")
        .then((data) => {
          setElements(data.data);
          setSelectedOption((selectedOption) => (selectedOption = true));
          Inputmask().mask(document.querySelectorAll("input"));
          showLoader((loading) => (loading = false));
        })
        .catch((error) => {
          toasterService.error((error?.response?.data?.message||"Something went wrong. Please try again."));
        });
    } else {
      // Call P form data
      showLoader(true);
      commonService
        .getFormFields("P", userDetails.role === Role.Internal ? "i" : "e")
        .then((data) => {
          setElements(data.data);
          setSelectedOption((selectedOption) => (selectedOption = false));
          Inputmask().mask(document.querySelectorAll("input"));
          showLoader((loading) => (loading = false));
        })
        .catch((error) => {
          toasterService.error((error?.response?.data?.message||"Something went wrong. Please try again."));
        });
    }
  }

  return (
    <div>
      <div className="form-container">
        {formSuccess && (
          <div className="form-success">
            <h4 className="display-6 text-center">
              Processing. Please don't close this window. Can take up to 2
              minutes.
            </h4>
            <div className="progress mt-4">
              <div
                className="progress-bar-fb"
                style={{ background: "#337ab7" }}
              ></div>
            </div>
          </div>
        )}
        {!formSuccess && (
          <RunHistoryLayout
            runs={runHistory.runs}
            activeRunId={runHistory.activeRunId}
            onSelect={handleSelectRun}
            onNew={runHistory.newRun}
          >
            {runHistory.activeRun && (
              <div>
                <h4>
                  <a
                    className="text-primary text-decoration-none"
                    href="javascript:;"
                    onClick={goBackToForm}
                  >
                    <i className="bi bi-arrow-left-circle"></i> Go back / New run
                  </a>
                </h4>
                {runHistory.activeRun.outputData ? (
                  <OutputReports outputData={runHistory.activeRun.outputData} />
                ) : (
                  <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading run…</span>
                    </div>
                  </div>
                )}
                {runHistory.activeRun.outputData && (
                  <div className="bf-submitbar">
                    <div className="bf-submitbar-inner">
                      <span className="bf-submitbar-text">
                        Indicate your preferred loan option, then submit it.
                      </span>
                      <a
                        className="btn btn-primary bf-btn-handoff bf-submitbar-btn"
                        href="https://buttonfinance.encompasstpoconnect.com/#/content/login"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Submit this loan →
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div hidden={!!runHistory.activeRun}>
              <div className="mb-3">
                <label className="me-2">Select an option</label>
                <BootstrapSwitchButton
                  checked={selectedOption}
                  onlabel="Calculate Interest Rate"
                  offlabel="Price Loan"
                  onChange={(val) => {
                    getFormFields(val);
                  }}
                  offstyle="primary"
                />
              </div>
              <hr />
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {!loading && fields.length > 0 && <EligibilityTip />}
                  {loading && (
                    <div key="loader" className="d-flex justify-content-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  {!loading && fields.length > 0 && (
                    <FormSections
                      fields={fields}
                      midSlot={
                        // Encompass pull is for ALL users; only on the
                        // interest tab (the price form is a different
                        // positional CSV contract).
                        selectedOption ? (
                          <EncompassPrefill fields={fields} onPull={setPulledLock} />
                        ) : null
                      }
                    />
                  )}

                  {!loading && fields.length > 0 && selectedOption &&
                    userDetails.role !== Role.Internal && (
                      <section className="bf-formsection">
                        <h6 className="bf-section-title">Pricing Date</h6>
                        <div className="mb-3">
                          <input
                            className={
                              "form-control" +
                              (pulledLock && pulledLock.lockDate
                                ? " bf-pricing-date-historical"
                                : "")
                            }
                            style={{ maxWidth: "260px" }}
                            value={
                              pulledLock && pulledLock.lockDate
                                ? pulledLock.lockDate + "  (loan lock date)"
                                : new Date().toLocaleDateString()
                            }
                            disabled
                            readOnly
                          />
                        </div>
                        <small className="bf-field-help">
                          {pulledLock && pulledLock.lockDate
                            ? "HISTORICAL: this quote will price on the lock date's rate sheet, and its run id will start with H."
                            : "Quotes price on today's rate sheet. Pulling a locked loan from Encompass prices it as of its lock date."}
                        </small>
                      </section>
                    )}

                  {!loading && fields.length > 0 && selectedOption &&
                    userDetails.role === Role.Internal && (
                      <section
                        className={
                          "bf-formsection" +
                          (methods.watch("exception_pricing") === "True"
                            ? " bf-beta-armed"
                            : "")
                        }
                      >
                        <h6 className="bf-section-title">BETA</h6>
                        <label className="form-label">Price Exceptions</label>
                        <div className="mb-3" style={{ maxWidth: "260px" }}>
                          {/* The engine's eligibility data defines what an
                              exception can price — never this dropdown. */}
                          <select
                            className={
                              "form-select" +
                              (methods.watch("exception_pricing") === "True"
                                ? " bf-exc-on"
                                : "")
                            }
                            defaultValue="False"
                            {...methods.register("exception_pricing")}
                          >
                            <option value="False">Off</option>
                            <option value="True">On</option>
                          </select>
                        </div>
                        <small
                          className={
                            "bf-field-help" +
                            (methods.watch("exception_pricing") === "True"
                              ? " bf-exc-warn"
                              : "")
                          }
                        >
                          Internal only — use at your own risk. Exception runs
                          are marked with an X run id and an exception banner
                          on the output.
                        </small>
                      </section>
                    )}

                  {!loading && fields.length > 0 && (
                    <div className="bf-submitbar">
                      <div className="bf-submitbar-inner">
                        <span className="bf-submitbar-text">
                          Review the details, then run your quote.
                        </span>
                        <input
                          className="btn btn-primary bf-submitbar-btn"
                          type="submit"
                          value="Price Loan"
                        />
                      </div>
                    </div>
                  )}
                </form>
              </FormProvider>
            </div>
          </RunHistoryLayout>
        )}
      </div>
    </div>
  );
}
